import { useCallback, useMemo } from 'react';
import { Form, type FormInstance } from 'antd';
import type { ShippingTypeOption } from '@components/shared/orders/types';

interface ShippingTypeBehavior {
  selectedShippingType: ShippingTypeOption | null;
  showCod: boolean;
  showDeliveryWithPickup: boolean;
  /** Pass to <Select onChange>. Clears `is_cod`/`delivery_with_pickup` when not supported. */
  handleShippingTypeChange: (value: string) => void;
  /** Manual orders only — sorted by name, platform-source filtered out. */
  filteredShippingTypes: ShippingTypeOption[];
}

/**
 * Derives the COD / delivery-with-pickup visibility flags from the form's
 * current `shipping_type_id` and provides a change handler that resets the
 * dependent checkboxes when the new shipping type doesn't support them.
 */
export function useShippingTypeBehavior(
  form: FormInstance,
  shippingTypes: ShippingTypeOption[],
): ShippingTypeBehavior {
  const shippingTypeValue = Form.useWatch('shipping_type_id', form);

  const selectedShippingType = useMemo(() => {
    if (!shippingTypeValue) return null;
    return shippingTypes.find(t => t.id === shippingTypeValue) || null;
  }, [shippingTypeValue, shippingTypes]);

  const handleShippingTypeChange = useCallback((value: string) => {
    const selected = shippingTypes.find(t => t.id === value);
    if (!selected?.supports_cod) {
      form.setFieldValue('is_cod', false);
    }
    if (!selected?.supports_delivery_with_pickup) {
      form.setFieldValue('delivery_with_pickup', false);
    }
  }, [shippingTypes, form]);

  const filteredShippingTypes = useMemo(() => {
    return shippingTypes
      .filter(t => t.source !== 'platform')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [shippingTypes]);

  return {
    selectedShippingType,
    showCod: selectedShippingType?.supports_cod || false,
    showDeliveryWithPickup: selectedShippingType?.supports_delivery_with_pickup || false,
    handleShippingTypeChange,
    filteredShippingTypes,
  };
}
